import { IconComponent } from '@/shared/components/icon/icon.component';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { BreadcrumbItem } from './breadcrumb.interface';

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent],
  providers: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <nav
      aria-label="Breadcrumb"
      class="p-2 bg-background border border-border rounded-lg "
      [class.hidden]=""
    >
      <ol class="flex flex-wrap items-center gap-1.5 wrap-break-words text-sm sm:gap-2.5">
        @for (item of visibleItems(); track item.label; let i = $index; let last = $last) {
          <!-- Separator (sauf avant le premier) -->
          @if (i > 0) {
            <li role="presentation" aria-hidden="true" class="flex items-center">
              <app-icon name="chevron-right" [size]="24" [strokeWidth]="1.5" />
            </li>
          }

          <!-- Ellipsis si tronqué -->
          @if (item.label === '__ellipsis__') {
            <li>
              <button
                (click)="expand()"
                class="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-700 transition-colors"
                aria-label="Afficher tout le chemin"
              >
                <app-icon name="ellipsis" [size]="16" />
              </button>
            </li>
          } @else {
            <li class="flex items-center">
              @if (!last && 'link' in item && item.link) {
                <!-- Lien cliquable -->
                <a
                  [routerLink]="item.link"
                  class="flex items-center gap-1.5 rounded-lg p-2  transition-colors hover:bg-background text-primary font-medium"
                >
                  @if ('icon' in item && item.icon) {
                    <app-icon [name]="item.icon" [size]="14" />
                  }
                  {{ item.label }}
                </a>
              } @else {
                <!-- Élément courant (dernier) -->
                <span
                  class="flex items-center gap-1.5 px-1 py-0.5 font-medium "
                  aria-current="page"
                >
                  @if ('icon' in item && item.icon) {
                    <app-icon [name]="item.icon" [size]="14" />
                  }
                  {{ item.label }}
                </span>
              }
            </li>
          }
        }
      </ol>
    </nav>
  `,
})
export class Breadcrumb {
  /** Liste des éléments du fil d'Ariane */
  @Input({ required: true }) items: BreadcrumbItem[] = [];

  /** Nb max d'items visibles avant ellipsis (0 = tous affichés) */
  @Input() maxItems: number = 0;

  expanded = false;

  expand() {
    this.expanded = true;
  }

  visibleItems(): (BreadcrumbItem | { label: '__ellipsis__' })[] {
    const { items, maxItems, expanded } = this;

    if (!maxItems || expanded || items.length <= maxItems) {
      return items;
    }

    // Toujours garder le premier et le dernier, ellipsis au milieu
    return [
      items[0],
      { label: '__ellipsis__' as const },
      ...items.slice(items.length - (maxItems - 2)),
    ];
  }
}
